import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { UiSettingsIdentifier, UiUserSettingsService } from './ui-user-settings.service';
import { catchError, debounceTime, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { merge } from 'lodash-es';
import { BackendErrorService } from './backend-error.service';
import { translationKeys } from '../core';
import { NGXLogger } from 'ngx-logger';

export interface ScipUiSettings {
  menu: MenuSettings;
  dashboard?: DashboardSettings;
}

export interface DashboardSettings {
  selectedOrganization?: {
    selectedOrgLabel: string;
    value: {
      name: string;
      id: string;
    };
  };
  tableTiles?: {
    show?: boolean;
    failedSubmissions?: TileSettings;
    productsWithoutSubmissions?: TileSettings;
  };
  chartTiles?: {
    show?: boolean;
    submissionsByStatus?: TileSettings;
    productsBySubmissionsStatus?: TileSettings;
    submissionByYear?: TileSettings;
    submissionByOrganisation?: TileSettings;
  };
  other?: {
    recentSearches: TileSettings;
  };
}

interface TileSettings {
  show?: boolean;
  collapsed?: boolean;
}

interface MenuSettings {
  collapsed?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ScipUiSettingsService {
  private autoSaveSettings: Subject<ScipUiSettings> = new Subject<ScipUiSettings>();
  private currentSettings: Subject<ScipUiSettings> = new ReplaySubject<ScipUiSettings>(1);

  private copySettings: ScipUiSettings = {
    menu: { collapsed: false },
    dashboard: {
      selectedOrganization: {
        selectedOrgLabel: 'All Organizations',
        value: {
          name: '',
          id: ''
        }
      },
      tableTiles: {
        show: true,
        failedSubmissions: {
          show: true,
          collapsed: false
        },
        productsWithoutSubmissions: {
          show: true,
          collapsed: false
        }
      },
      chartTiles: {
        show: true,
        submissionsByStatus: {
          show: true
        },
        productsBySubmissionsStatus: {
          show: true
        },
        submissionByYear: {
          show: false
        },
        submissionByOrganisation: {
          show: false
        }
      },
      other: {
        recentSearches: {
          show: true,
          collapsed: false
        }
      }
    }
  };

  private readonly uiSettings: Observable<ScipUiSettings> = this.uiUserSettingsService.getUiSettingsFor(UiSettingsIdentifier.ScipUiSettings).pipe(
    map((dbSettings: ScipUiSettings) => dbSettings), // satisfy typescript parser -.-
    map((dbSettings) => {
      if (!dbSettings) {
        return this.copySettings; // If user never changed anything, give him the default settings
      } else {
        // Merge the default with the ones from the backend
        // Could be that the backend settings e.g. do not contain a newly added tile ;)
        // Need to do a "deep"-merge, as we could extend the lower tiles-categories with new tiles
        return merge(this.copySettings, dbSettings); // Use the settings the user saved in the past
      }
    }),
    map(() => this.copySettings), // A new subscriber should get the copy as there could be recent changes?
    tap((settings) => this.currentSettings.next(settings)),
    shareReplay(1), // Only load it once from the db, only do merge/etc. once
    catchError((error) => {
      this.currentSettings.next(this.copySettings);
      this.logger.error('ScipUiSettingsService, the settings could not be loaded', error);
      return of(this.copySettings); // We could not load scipUiSeting, so lets handle it like if there is nothing saved
    }),
    switchMap(() => this.currentSettings)
  );

  constructor(private uiUserSettingsService: UiUserSettingsService, private backendErrorService: BackendErrorService, private logger: NGXLogger) {
    this.autoSaveSettings
      .asObservable()
      .pipe(
        debounceTime(2000),
        switchMap((newSettings) => this.saveSettings(newSettings)),
        catchError((error) => {
          // Currently, it's not defined what we should do? But as it is not "urgent", we do not notify the user about it
          // Worst case, e.g. the menu is still collapsed next time he opens the application
          this.logger.error('ScipUiSettingsService, the settings could not be saved', error);
          return of(false);
        })
      )
      .subscribe();
  }

  getScipUiSettings(): Observable<ScipUiSettings> {
    // Gets the current user ui settings or, if no change made, the default settings we defined
    return this.uiSettings;
  }

  saveDashboardSettings(dashboard: DashboardSettings): Observable<boolean> {
    this.copySettings = {
      ...this.copySettings,
      dashboard
    };
    return this.saveSettings(this.copySettings).pipe(
      catchError((error) => {
        this.backendErrorService.handleError(error, translationKeys.dashboard.userSettingsComponent.typescript.save.error);
        return of(false);
      }),
      tap((saved) => {
        if (saved) {
          this.currentSettings.next(this.copySettings);
        }
      })
    );
  }

  saveMenuSettings(menu: MenuSettings): void {
    this.autoSaveSettings.next({
      ...this.copySettings,
      menu
    });
  }

  private saveSettings(newState: ScipUiSettings): Observable<boolean> {
    return this.uiUserSettingsService.saveUiSettingsFor(UiSettingsIdentifier.ScipUiSettings, newState).pipe(
      tap((saved) => {
        if (saved) {
          this.copySettings = newState;
        }
      })
    );
  }
}
